<template>
	<v-container class="space-y-2xl px-sm pt-sm pb-xl md:px-md md:pt-lg lg:px-lg">
		<v-h3 tag="h1"> {{ t("search-page-title") }} "{{ searchQuery }}" </v-h3>

		<v-elevate-product-list
			v-slot="{
				products,
				productCount,
				hasNextPage,
				loadMore,
				alternativeProducts,
				activeFiltersCount,
				clearFilter,
			}"
			type="search"
			:origin="origin"
			:query="searchQuery"
		>
			<p class="mb-md font-sans text-sm md:text-base">
				{{ t("search-found", {n: productCount ?? 0}) }}
				<span v-if="articles.length > 0">
					{{ t("and") }}
					<button
						type="button"
						class="underline"
						@click="scrollToArticles"
						@keydown.enter="scrollToArticles"
					>
						{{ t("search-articles", {n: articles.length}) }}
					</button>
				</span>
			</p>

			<template v-if="products && products.length > 0">
				<div class="mb-xl space-y-lg">
					<p class="mb-lg font-prenton-cond text-xl lg:text-5xl">
						{{ t("products-for-search") }} "{{ searchQuery }}"
					</p>

					<div class="flex w-full flex-col justify-between">
						<p class="text-sm tracking-xl text-gray-500 md:block">
							{{ productCount }} {{ t("amount-products") }}
						</p>

						<div class="mt-sm flex gap-lg lg:pt-md">
							<v-button
								type="button"
								theme="black"
								size="sm"
								class="flex items-center space-x-xs hover:underline"
								@click="isModalOpen = true"
							>
								<template v-if="activeFiltersCount > 0">
									<v-category-filter-count
										:active-filters-count="activeFiltersCount"
									/>
								</template>

								<span>{{ tg("filter-title") }}</span>

								<v-icon icon="filter" class="text-lg"></v-icon>
							</v-button>

							<v-button
								v-if="activeFiltersCount > 0"
								theme="gray"
								size="sm"
								@click="clearFilter"
							>
								{{ t("filter-clear") }}
							</v-button>
						</div>

						<v-elevate-common-facets class="mt-xs" />

						<div class="mt-md flex items-center justify-between gap-sm">
							<v-category-order />

							<v-category-preferred-image-type-toggle />
						</div>
					</div>

					<div
						class="grid grid-cols-2 gap-x-sm gap-y-lg md:grid-cols-3 md:gap-y-xl lg:grid-cols-4"
					>
						<v-elevate-listing-product-card
							v-for="item in products"
							:key="item.key"
							:product="item"
						/>
					</div>
				</div>

				<div class="mb-xl grid md:grid-cols-8">
					<div class="space-y-md md:col-span-2 md:col-start-4">
						<v-button
							class="w-full"
							:theme="hasNextPage ? 'black' : 'gray'"
							:disabled="!hasNextPage"
							:aria-label="
								hasNextPage
									? $t('loader.show-more')
									: $t('loader.show-more-loaded')
							"
							@click="loadMore"
						>
							{{
								hasNextPage
									? $t("loader.show-more")
									: $t("loader.show-more-loaded")
							}}
						</v-button>

						<div class="space-y-xs">
							<div class="h-[2px] rounded-full bg-gray-300">
								<div
									class="h-[2px] rounded-full bg-black"
									:style="{
										width: `${(products.length / productCount) * 100}%`,
									}"
								></div>
							</div>

							<p class="text-center">
								{{ $t("loader.show-more-prefix") }} {{ products.length }} /
								{{ productCount }} {{ $t("loader.show-more-suffix") }}
							</p>
						</div>
					</div>
				</div>
			</template>

			<v-scrollable
				v-if="alternativeProducts && alternativeProducts.length > 0"
				class="flex snap-x scroll-px-sm flex-nowrap space-x-xs px-sm md:scroll-px-0 md:space-x-sm md:px-0"
			>
				<template #header="{move}">
					<v-scrollable-header
						:title="t('alternative-products-title')"
						class="mx-sm md:mx-0"
						@move="move"
					/>
				</template>

				<v-scrollable-item
					v-for="product in alternativeProducts"
					:key="product.key"
					class="w-40 shrink-0 snap-start md:w-65"
				>
					<v-elevate-listing-product-card
						:product="product"
						:is-search="false"
					/>
				</v-scrollable-item>
			</v-scrollable>

			<div ref="articlesAnchor"></div>

			<div
				v-if="articlesLoading"
				class="mb-lg flex flex-row items-center font-prenton-cond text-xl lg:text-2xl"
			>
				<v-spinner />

				<p>{{ t("loading-articles") }}</p>
			</div>

			<div v-else-if="articles.length > 0" class="mb-lg">
				<p class="mb-lg font-prenton-cond text-xl lg:text-5xl">
					{{ t("articles-results") }}
					"{{ searchQuery }}"
				</p>

				<div
					class="grid grid-cols-2 gap-x-sm gap-y-lg md:grid-cols-3 md:gap-y-xl lg:grid-cols-4"
				>
					<v-page-search-article
						v-for="(article, index) in articles"
						:key="index"
						:article="article"
					/>
				</div>
			</div>

			<v-category-filter-modal
				:is-open="isModalOpen"
				:results="productCount"
				@close="isModalOpen = false"
			/>
		</v-elevate-product-list>
	</v-container>
</template>

<script setup lang="ts">
import {useDefaultDynamicHead} from "@pkg/shared/composables/use-default-dynamic-head";
import {useLifecycle} from "@pkg/shared/composables/use-lifecycle";
import {useStoryblokArticles} from "@pkg/shared/composables/use-storyblok-articles";

const {t} = useI18n();
const {t: tg} = useI18n({
	useScope: "global",
});

const route = useRoute();
const origin = computed(() => route.query.origin?.toString());

const {isMounted} = useLifecycle();

const searchQuery = computed(() => route.query.query?.toString());

const {data: articlesRes, pending: articlesLoading} = useStoryblokArticles(
	{
		perPage: 20,
		searchTerm: searchQuery,
	},
	{
		pause: computed(() => !isMounted.value || !searchQuery.value),
	},
);

const articles = computed(() => articlesRes.value?.stories ?? []);

const articlesAnchor = ref<HTMLDivElement>();

function scrollToArticles() {
	if (articlesAnchor.value) {
		articlesAnchor.value.scrollIntoView({
			behavior: "smooth",
			block: "center",
			inline: "nearest",
		});
	}
}

watch(searchQuery, () => {
	window.scrollTo({top: 0, behavior: "smooth"});
});

const isModalOpen = ref(false);

useDefaultDynamicHead({includeQueryInCanonicalUrl: false});

useSeoMeta({
	title: t("page-title"),
	description: t("page-description"),
	ogTitle: t("page-title"),
	ogDescription: t("page-description"),
	ogImage: "https://cmd-prod.s3-eu-west-1.amazonaws.com/logo-masku.svg",
});
</script>

<!-- eslint-disable max-len -->
<i18n lang="json">
{
	"et": {
		"page-title": "Vali endale meelepärane toode tuhandete mööbliesemete seast",
		"page-description": "Maskust leiad lahendused sisustuseks alates turu soodsaimatest kuni tippbrändideni. Tutvu ja armu meie valikusse!",
		"search-page-title": "Näitab tulemusi otsingule",
		"search-found": "Leitud {n} toodet",
		"and": "ja",
		"search-articles": "{n} artiklit",
		"amount-products": "toodet",
		"articles-results": "Artiklid otsingule",
		"products-for-search": "Tooted otsingule",
		"alternative-products-title": "Vaata ka neid",
		"title": "Otsing",
		"loading-articles": "Artiklite laadimine.."
	},
	"fi": {
		"page-title": "Hae mieleisesi tuhansien huonekalujen valikoimasta",
		"page-description": "Maskusta löydät ratkaisut sisustukseen markkinoiden edullisimmista aina huippubrändeihin asti. Tutustu ja ihastu valikoimaamme!",
		"search-page-title": "Näytetään hakutulokset haulle",
		"search-found": "Löysimme {n} tuotetta",
		"and": "ja",
		"search-articles": "{n} artikkelia",
		"amount-products": "tuotetta",
		"articles-results": "Artikkelit haulle",
		"products-for-search": "Tuotteet haulle",
		"alternative-products-title": "Voisit olla kiinnostunut näistä",
		"title": "Haku",
		"loading-articles": "Ladataan artikkeleita.."
	}
}
</i18n>
